import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Spinner, Table } from 'react-bootstrap';
import { apiGet, apiPatch, apiPost } from '../../services/api';

const SKILL_CATEGORIES = [
  'Frontend',
  'Backend',
  'Database',
  'Mobile',
  'DevOps & Cloud',
  'QA & Testing',
  'AI & ML',
  'Data',
  'Design',
  'Product & Project',
  'Marketing',
  'Security',
  'Finance & Legal',
  'Content & Writing',
  'General',
];

export default function AdminSkillsTab({ token, isActive, handleAuthError, setError }) {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('Frontend');
  const [submitting, setSubmitting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const loadSkills = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setFetchError('');
    try {
      const res = await apiGet('/api/v1/admin/skills', token);
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setFetchError('Could not load skills.');
        return;
      }
      const json = await res.json();
      setSkills(Array.isArray(json.data) ? json.data : []);
    } catch {
      setFetchError('Could not load skills.');
    } finally {
      setLoading(false);
    }
  }, [token, handleAuthError]);

  useEffect(() => {
    if (isActive) {
      loadSkills();
    }
  }, [isActive, loadSkills]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token || !name.trim()) return;
    setSubmitting(true);
    setSubmitError('');
    setSuccess('');
    setError('');
    try {
      const res = await apiPost(
        '/api/v1/admin/skills',
        { name: name.trim(), category },
        token
      );
      if (handleAuthError(res)) return;
      if (res.status === 422) {
        const data = await res.json();
        setSubmitError(
          data?.errors?.name?.[0] || data?.errors?.category?.[0] || 'Could not create skill.'
        );
        return;
      }
      if (!res.ok) {
        setSubmitError('Could not create skill.');
        return;
      }
      setName('');
      setSuccess('Skill created.');
      await loadSkills();
    } catch {
      setSubmitError('Could not create skill.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (skillId) => {
    if (!token) return;
    setTogglingId(skillId);
    setSuccess('');
    setSubmitError('');
    setError('');
    try {
      const res = await apiPatch(`/api/v1/admin/skills/${skillId}/toggle`, {}, token);
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setFetchError('Could not update skill status.');
        return;
      }
      const json = await res.json();
      const updated = json.data;
      setSkills((prev) =>
        prev.map((row) => (row.id === skillId ? { ...row, ...updated } : row))
      );
      setSuccess('Skill status updated.');
    } catch {
      setFetchError('Could not update skill status.');
    } finally {
      setTogglingId(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4" data-testid="rwm-admin-skills-loading">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <div className="rwm-admin-skills">
      {fetchError ? (
        <Alert variant="danger" data-testid="rwm-admin-skills-error">
          {fetchError}
        </Alert>
      ) : null}
      {submitError ? (
        <Alert variant="danger" data-testid="rwm-admin-skills-error">
          {submitError}
        </Alert>
      ) : null}
      {success ? (
        <Alert variant="success" data-testid="rwm-admin-skills-success">
          {success}
        </Alert>
      ) : null}

      <Form onSubmit={handleSubmit} className="mb-4">
        <h2 className="h5 mb-3">Add Skill</h2>
        <div className="row g-2 align-items-end">
          <div className="col-md-5">
            <Form.Group>
              <Form.Label className="small text-muted mb-1">Name</Form.Label>
              <Form.Control
                type="text"
                value={name}
                maxLength={100}
                onChange={(e) => setName(e.target.value)}
                data-testid="rwm-admin-skill-name"
              />
            </Form.Group>
          </div>
          <div className="col-md-5">
            <Form.Group>
              <Form.Label className="small text-muted mb-1">Category</Form.Label>
              <Form.Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="rwm-admin-skill-category"
              >
                {SKILL_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-md-2">
            <Button
              type="submit"
              variant="info"
              className="w-100 text-white"
              disabled={submitting || !name.trim()}
              data-testid="rwm-admin-skill-submit"
            >
              {submitting ? 'Adding…' : 'Add Skill'}
            </Button>
          </div>
        </div>
      </Form>

      <div className="table-responsive" data-testid="rwm-admin-skills-table">
        <Table className="align-middle mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {skills.map((skill) => (
              <tr key={skill.id}>
                <td>{skill.name}</td>
                <td>{skill.category}</td>
                <td>
                  {skill.is_active ? (
                    <Badge bg="success">Active</Badge>
                  ) : (
                    <Badge bg="secondary">Inactive</Badge>
                  )}
                </td>
                <td className="text-end">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={togglingId === skill.id}
                    onClick={() => handleToggle(skill.id)}
                    data-testid={`rwm-admin-skill-toggle-${skill.id}`}
                  >
                    {togglingId === skill.id
                      ? 'Updating…'
                      : skill.is_active
                        ? 'Deactivate'
                        : 'Activate'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
}
